/**
 * The one sentence that explains why nothing can be removed from a published
 * week — stated once for the week, not once per session.
 *
 * **Why it lives in its own module.** The sentence is rendered by `WeekCard`
 * (`ClientPlanPage.tsx`) and pointed at by every disabled removal control inside
 * `SessionEditor.tsx`. Two files have to agree on one element id, and an id
 * invented independently in each is an `aria-describedby` that silently points
 * at nothing — the exact failure the arrangement exists to avoid.
 *
 * **Why a sentence and not a tooltip.** Hover-only, unreliable to assistive
 * technology, invisible on touch. And why one sentence rather than six disabled
 * buttons each having to answer "why?" on its own: a screen reader reaching any
 * one of six unavailable controls in a week gets the reason once, from here
 * (design D6).
 *
 * **Why the control is disabled rather than hidden.** A control that vanishes
 * teaches nothing, and the coach concludes the product cannot delete sessions at
 * all — which is precisely the misreading that produced this round of feedback.
 *
 *   design-system, "An unavailable action teaches the rule instead of disappearing"
 *   design-system, "The reason is given once and reaches a screen reader"
 *   plan-editing, "The reason is given once for the week, not once per session"
 */

/** The id the notice carries, and that every disabled control points at. */
export function publishedWeekNoticeId(weekId: string): string {
  return `published-week-notice-${weekId}`;
}

/**
 * The sentence itself.
 *
 * The client is named from her own row rather than written into the source —
 * `pnpm check:names` forbids a person's name as a literal here, and a real name
 * is what makes the consequence concrete anyway.
 */
export function publishedWeekNotice(clientName: string): string {
  return (
    `This week is published. ${clientName} can see it, so nothing here can be removed ` +
    `while it stays published. You can still add and edit, or take the week back to draft.`
  );
}

export function PublishedWeekNotice({
  weekId,
  clientName,
}: {
  weekId: string;
  clientName: string;
}) {
  return (
    <p
      id={publishedWeekNoticeId(weekId)}
      className="text-ink-soft bg-surface-muted mt-3 rounded-[--radius-control] px-3 py-2 text-sm leading-relaxed"
    >
      {publishedWeekNotice(clientName)}
    </p>
  );
}
